import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PIEText from '@src/shared/components/PIEText';
import DeliveryDashboardSideCard from '../DeliveryDashboardSideCard';
import {
  CourierReturnStatusType,
  DeliveryDashboardScreens,
  DeliveryStatus,
  OrderStatus,
} from '../../models/model-enum';
import { ICourierSnap, IOrder, IRoute, IWaypoint } from '../../models/model-interface';
import DeliveryDashboardBottomSheet from '../DeliveryDashboardBottomSheet';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { globalStyles } from 'snacks-design-system';
import { courierStatusTranslationKeys, Statuses } from '../../models/model-constants';
import DashboardCustomTag from '../DashboardCustomTag';
import useDashboardStore from '../../hooks/use-dashboard-store';
import DashboardSideCardContentAutoFit from '../DashboardSideCardContentAutoFit';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import useDashboardComputedFunctions from '../../hooks/use-dashboard-computed';
import useEtaBack from '../../hooks/use-eta-back';
import { useApplicationUiStore } from '@src/shared/store/ui-store/application-ui-store';
import { CourierUpcomingDelivery } from './components/CourierUpcomingDelivery';
import CourierCurrentDelivery from './components/CourierCurrentDelivery';
import dayjs from 'dayjs';
import { IconLocationPinFilled } from '@justeattakeaway/pie-icons-react';

const { xdsSpacingD, xdsSpacingB, xdsColorBackgroundDefault } = globalStyles;
interface componentProps {
  courierStatus: any;
}
export const DashboardCourierDetail = ({ courierStatus }: componentProps) => {
  const { isResolutionMobile } = useDeviceDetect();
  const { t } = useTranslation();
  const { activeCourierId, clearActiveCourier, setActiveScreen } = useDashboardUIStore();
  const { couriersList, orderList, courierRoute, courierNextRoute } = useDashboardStore();
  const { getCourierEtaById, getOrderEDTByUUID } = useDashboardComputedFunctions();
  const { getDistanceDuration } = useEtaBack();
  const { appDomElementClientHeightRef } = useApplicationUiStore();
  const [isOrderDelayedInPool, setIsOrderDelayedInPool] = useState(false);

  const renderCourierDetail = (courierId: string): ICourierSnap | undefined => {
    if (courierId) {
      const retrievedCourier = couriersList?.find((c: ICourierSnap) => c.id === courierId);
      if (!retrievedCourier) {
        return;
      }
      return retrievedCourier;
    }
  };
  const couriRoute =
    courierRoute && (courierRoute.find((r: IRoute) => r.courier.id === activeCourierId) as IRoute);
  const deliveredOrderCount =
    couriRoute && couriRoute?.waypoints.filter((w: IWaypoint) => w.isDelivered).length + 1;
  const totalOrderCount = couriRoute && couriRoute?.waypoints?.length;
  const getOrderUUIDforDelievered =
    couriRoute &&
    couriRoute.waypoints.filter((w: IWaypoint) => !w.isDelivered).map((o) => o.order.uuid);

  const currentRoutePoints = courierRoute?.find(
    (r: IRoute) => r.courier.id === activeCourierId
  )?.waypoints;
  const nextRoutePoints = courierNextRoute?.find(
    (r: IRoute) => r.courier.id === activeCourierId
  )?.waypoints;

  const orderRef = currentRoutePoints?.map((w) => w.order.reference) as any;
  const filteredOrderList = orderList
    ?.filter((o: IOrder) =>
      currentRoutePoints?.map((w: IWaypoint) => w.order.uuid).includes(o.uuid)
    )
    .filter(
      (o) => o.status !== OrderStatus.Unassigned && o.status !== OrderStatus.AssignedToNextPool
    )
    .sort(
      (a, b) =>
        orderRef?.findIndex((m: string) => m === a?.reference) -
        orderRef?.findIndex((m: string) => m === b?.reference)
    );
  const filterAssignedNextPool = orderList
    ?.filter((o: IOrder) => nextRoutePoints?.map((w: IWaypoint) => w.order.uuid).includes(o.uuid))
    ?.filter((o: IOrder) => o.status === OrderStatus.AssignedToNextPool);

  useEffect(() => {
    getOrderUUIDforDelievered &&
      getOrderUUIDforDelievered.forEach((uuid) => {
        if (getOrderEDTByUUID(uuid) && dayjs(getOrderEDTByUUID(uuid) as any).isBefore(dayjs())) {
          !isOrderDelayedInPool && setIsOrderDelayedInPool(true);
        }
      });

    return () => {
      setIsOrderDelayedInPool(false);
    };
  }, [courierRoute]);

  const renderContent = () => {
    return (
      <>
        {filteredOrderList && filteredOrderList?.length > 0 ? (
          <>
            <CourierCurrentDelivery
              data={filteredOrderList as IOrder[]}
              title={
                filterAssignedNextPool && filterAssignedNextPool?.length > 0
                  ? t('order.current_delivery')
                  : ''
              }
            />
            {filterAssignedNextPool && filterAssignedNextPool?.length > 0 ? (
              <CourierUpcomingDelivery
                title={t('order.upcoming_delivery')}
                data={filterAssignedNextPool as IOrder[]}
              />
            ) : null}
          </>
        ) : (
          // ERROR SCREEN
          <PIEComponentUtil
            paddingTop={xdsSpacingD}
            paddingLeft={xdsSpacingB}
            paddingBottom={xdsSpacingD}
          >
            <PIEText
              text={t('dashboard.NO_ORDERS')}
              size="var(--xds-font-size-16)"
              align="left"
              allowCaps={true}
              fontWeight="var(--xds-font-weight-regular)"
            />
          </PIEComponentUtil>
        )}
      </>
    );
  };

  return (
    (!isResolutionMobile && (
      <DeliveryDashboardSideCard
        header={
          <>
            <PIEText
              size="var(--xds-font-size-20)"
              align="left"
              elipsis={true}
              truncate={true}
              text={
                <div
                  style={{ display: 'flex', flexDirection: 'column', width: '295px', gap: '5px' }}
                >
                  {renderCourierDetail(activeCourierId as string)?.firstName}
                  <br />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      justifyContent: 'space-between',
                    }}
                  >
                    <DashboardCustomTag
                      tagType={courierStatus}
                      modStrong={courierStatus === Statuses.Returning}
                      text={t(
                        courierStatus === CourierReturnStatusType.Returning
                          ? `${getDistanceDuration(getCourierEtaById(activeCourierId as string))}`
                          : courierStatusTranslationKeys[courierStatus]
                      )}
                    />

                    {filteredOrderList?.length &&
                    courierStatus !== DeliveryStatus.Assigned &&
                    totalOrderCount &&
                    totalOrderCount > 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginLeft: 'auto',
                        }}
                      >
                        <IconLocationPinFilled size="xs" />
                        <PIEText
                          truncate={false}
                          fontWeight="var(--xds-font-weight-regular)"
                          size="var(--xds-font-size-14)"
                          text={`
                            ${`${
                              totalOrderCount === 1
                                ? t('courier.status.SINGLE')
                                : t('courier.status.STEP')
                            }  ${
                              totalOrderCount === 1
                                ? ''
                                : `${
                                    courierStatus !== DeliveryStatus.Returning &&
                                    courierStatus !== DeliveryStatus.Arrived &&
                                    courierStatus
                                      ? deliveredOrderCount
                                      : (deliveredOrderCount || 0) - 1
                                  } of ${totalOrderCount}`
                            }`}
                            `}
                        />
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              }
              fontWeight="var(--xds-font-weight-bold)"
            ></PIEText>
          </>
        }
        showBackButton={true}
        onBackButton={() => {
          clearActiveCourier();
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
        }}
      >
        <PIEComponentUtil
          bgColor={xdsColorBackgroundDefault}
          paddingLeft={xdsSpacingD}
          paddingRight={xdsSpacingD}
        >
          <DashboardSideCardContentAutoFit
            config={{
              documentNavigationHeaderHeight:
                appDomElementClientHeightRef.appDomElementNavigationHeader as number,
              sectionOffsetA: appDomElementClientHeightRef.domElementOffsetA as number,
              contentItemHeight: 148,
            }}
          >
            {renderContent()}
          </DashboardSideCardContentAutoFit>
        </PIEComponentUtil>
      </DeliveryDashboardSideCard>
    )) || (
      <DeliveryDashboardBottomSheet
        showCloseButton={true}
        onClose={() => {
          clearActiveCourier();
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
        }}
        onBottomSheetHeight={() => {}}
        initialSnap={({ maxHeight }) => maxHeight / 2}
        isDetailView={true}
        header={
          <>
            <PIEText
              size="var(--xds-font-size-20)"
              align="left"
              text={renderCourierDetail(activeCourierId as string)?.firstName}
              elipsis={true}
              truncate={true}
              fontWeight="var(--xds-font-weight-bold)"
            ></PIEText>
            <DashboardCustomTag
              tagType={courierStatus}
              modStrong={courierStatus === Statuses.Returning}
              text={
                <PIEText
                  size="m"
                  truncate={false}
                  text={
                    courierStatus === CourierReturnStatusType.Returning
                      ? getDistanceDuration(getCourierEtaById(activeCourierId as string))
                      : t(courierStatusTranslationKeys[courierStatus])
                  }
                />
              }
            />
          </>
        }
      >
        <PIEComponentUtil paddingX={xdsSpacingD}>{renderContent()}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    )
  );
};

export default DashboardCourierDetail;
